import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { SubmitDialogeComponent } from 'src/app/common/components/submit-dialoge/submit-dialoge.component';
import { CommonserviceService } from 'src/app/common/services/commonservice.service';
import { Address, AddressData, UserPreference } from 'src/app/common/models/loginPreference.model';
import { apiEndPoints, apiQueryParams } from 'src/app/common/enums/common.enum';
import { DataService } from 'src/app/common/services/data.service';
import { DecodedSSOToken } from 'src/app/common/models/decodedSsoToken';
import { SAVE_USER_PROFILE } from 'src/app/common/graphql/queries';
import { getNamcIdForName, getNamcIdForNameInt, getTodayDate } from 'src/app/common/custom.method';
import { SpinnerService } from 'src/app/common/services/spinner.service';
import { Subscription } from 'rxjs';
import { ErrorHandlerService } from 'src/app/common/services/error-handler.service';
import { userDetailsSignal } from 'src/app/common/signals/user-profile-signal';

@Component({
  selector: 'app-login-preference',
  templateUrl: './login-preference.component.html',
  styleUrls: ['./login-preference.component.css']
})
export class LoginPreferenceComponent implements OnInit, OnDestroy {

  languageSelection = [
    { name: 'English', value: 'en' },
    { name: 'Spanish', value: 'es' }
  ];

  plants:string[] = []
  shops:string[] = [];
  lineNames:string[] = []
  userForm: FormGroup;
  responseBody:AddressData[] = [];
  errorList:string[] = [];
  userLineDetails:UserPreference;
  userSsoDetails: DecodedSSOToken;
  userDetails: DecodedSSOToken;
  plantSubscription:Subscription;
  shopSubscription:Subscription;
  namcId:string = '';
  workDayId: string  ='';
  firstName: string = '';
  constructor(
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly translate: TranslateService,
    public dialog: MatDialog,
    private readonly service : CommonserviceService,
    private readonly dataService: DataService,
    private readonly spinner: SpinnerService,
    private readonly errorService: ErrorHandlerService
  ) {}

  ngOnInit(): void {
    this.errorService.clearErrorMessage();
    const userSsoDetails = sessionStorage.getItem('userDetails');
    if (userSsoDetails) {
      this.userSsoDetails = JSON.parse(userSsoDetails) as DecodedSSOToken;
    }
    this.firstName = userDetailsSignal().firstName;
    this.namcId = getNamcIdForName(this.getNamcName());
    this.workDayId = this.dataService.getWorkDayId();
    this.userForm = this.fb.group({
      plant: ['', Validators.required],
      shop: ['', Validators.required],
      lineName: ['', Validators.required],
      language: ['en', Validators.required]
    });
    // Call main API to get the address configuration
    this.callPieGetAddrConfig(this.namcId);

    // get shop & line name based on plant selection
    this.plantSubscription = this.userForm.controls['plant'].valueChanges.subscribe((value) => {
      const shop = this.responseBody.filter(item => item.plant_name === value);
      this.shops = [...new Set(shop.map(item => item.shop_name))];
      this.lineNames = [...new Set(shop.map(item => (item.line_name)))];
      this.userForm.patchValue({
        shop : '',
        lineName : ''
      })
      this.userForm.get('shop').markAsUntouched();
      this.userForm.get('lineName').markAsUntouched();
    });
    // get line name based on shop selection
    this.shopSubscription = this.userForm.controls['shop'].valueChanges.subscribe((value) => {
      this.userForm.patchValue({
        lineName : ''
      })
      this.userForm.get('lineName').markAsUntouched();
      const lineName = this.responseBody.filter(item => item.shop_name === value);
      this.lineNames = [...new Set(lineName.map(item => (item.line_name)))].sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }));
    });
  }


  callPieGetAddrConfig(business_entity:string): void {
    const query = `${apiEndPoints.GetAdrConfig}?${apiQueryParams.business_entity}=${business_entity}`;
    this.spinner.show()
    // call the service to get the address configuration
    this.service.getPieData(query).subscribe({
      next : (res:Address) => {
        if(res?.httpStatusCode === '200' && res?.hasError === 'false' && res?.responseBody.length > 0) {
          this.responseBody = res.responseBody;
          this.plants = [...new Set(res.responseBody.map(item => item.plant_name))];
          this.errorList = [];
          this.getLoggedInUserDetails();
        }else{
          this.errorService.setErrorMessage([]);
        }
        this.spinner.hide();
      },
      error : (err:any) => {
        this.spinner.hide();
        this.errorService.setErrorMessage([]);
      },
      complete : () => {
        this.spinner.hide();
      }
    })
  }


  getLoggedInUserDetails():void{
    this.dataService.getUserProfile().subscribe((response) =>{
      if(response) {
        this.userLineDetails = response;
        this.setValuesFromObservable(this.userLineDetails);
      }
    })
  }

  setValuesFromObservable(preferences:UserPreference): void {
    this.userForm.patchValue({
      plant: preferences.plantName,
      shop: preferences.shopName,
      lineName: preferences.lineName.split('|'),
    });

    // Trigger value changes manually for plant, shop, and lineName to update dependent fields
    this.userForm.controls['plant'].setValue(preferences.plantName);
    this.userForm.controls['shop'].setValue(preferences.shopName);
    this.userForm.controls['lineName'].setValue(preferences.lineName.split('|'));
  }

  onDetailSaved(): void {
    this.errorList = [];
    this.userForm.markAllAsTouched();
    if(this.userForm.valid) {
      const selectedLanguage = this.userForm.get('language')?.value;
      localStorage.setItem('language', selectedLanguage); // Save language preference
      this.translate.use(selectedLanguage); // Set the selected language globally
      // save userpreference is session storage for dev purppose. Replace once API integration is done
      sessionStorage.setItem('userPreference', JSON.stringify(this.userForm.value));
      this.dataService.setUserPreferences(this.userForm.value);
      const userDetailsString = sessionStorage.getItem('userDetails');
      if (userDetailsString) {
        this.userDetails = JSON.parse(userDetailsString) as DecodedSSOToken;
      }
      const input = {
        userId: userDetailsSignal().userId,
        plantName: this.userForm.get('plant')?.value || '',
        shopName: this.userForm.get('shop')?.value || '',
        lineName: this.userForm.get('lineName')?.value.join('|'),
        namc: getNamcIdForNameInt(this.getNamcName()) || '',
        title: this.dataService.getUserRoleId(),
        crteDt: getTodayDate(),
        crteBy: userDetailsSignal().workDayId.toString(),
        updtDt: getTodayDate(),
        updtBy: userDetailsSignal().workDayId.toString(),
        usrRoleId: this.dataService.getUserRoleId(),
        dckCd: '',
        shpLnNm: ''
      };
      this.spinner.show();
      this.service.getHcmData(SAVE_USER_PROFILE, { input }).subscribe({
        next: (response) => {
        if (response?.data?.saveUserProfile?.message == 'User profile saved successfully') {
          this.dataService.setUserProfile({...this.userLineDetails, plantName : this.userForm.get('plant')?.value, shopName : this.userForm.get('shop')?.value, lineName : this.userForm.get('lineName')?.value.join('|')});
          this.translate.get(['SaveInfo.message']).subscribe(translations => {
            const dialogRef = this.dialog.open(SubmitDialogeComponent, {

              panelClass: 'centered-dialog',
              data: {
                message: translations['SaveInfo.message'],
                showOkButton: true,
                showSuccess: true,
                action: "Save Profile",
                redirectTo: '/view-profile'
              }
            });
            dialogRef.afterClosed().subscribe((result: boolean | undefined) => {
              if (result) {
                this.router.navigate(['/view-profile']);
              }
            });
          });
        }
        if(response?.errors){
          this.spinner.hide();
          this.errorService.setErrorMessage([]);
        }
        this.spinner.hide();
        },
        error: (error) => {
          this.spinner.hide();
          this.errorService.setErrorMessage([]);
        },
      });
    }
  }
  public navigateToHome(): void {
    this.router.navigate(['home'])
  }

  public onDetailCanceled(): void {
    // this.router.navigate(['/view-profile'])
      this.translate.get(['unSaveInfo.title', 'unSaveInfo.message']).subscribe(translations => {
        const dialogRef = this.dialog.open(SubmitDialogeComponent, {
          width: '567px',
          panelClass: 'centered-dialog',
          data: {
            message: translations['unSaveInfo.message'],
            title:translations['unSaveInfo.title'],
            showCancelButton: true,
            action: "Cancel Hot Call",
            showYesButton: true,
          }
        });

        dialogRef.afterClosed().subscribe((result: boolean | undefined) => {
          if (result === true) {
            this.router.navigate(['/view-profile']);
          }
        });
      });

  }

  showActionButtons(): boolean {
    return !!sessionStorage.getItem('userPreference');
  }

  // Common fucntion to check the form validation
  checkAndMarkAsTouched(fieldName: string): boolean {
    const field = this.userForm.get(fieldName);
    if (!field || field.invalid) {
      field?.markAsTouched();
      return true;
    }
    return false;
  }

  getNamcName(): string {
    let namc = '';
    this.service.getNamc().subscribe({
      next: (response) => {
        namc = response;
      },
      error: (error) => {
        console.error('getNacm Error:', error);
      }
    });
    return namc;
  }

  ngOnDestroy(): void {
    if(this.plantSubscription) {
      this.plantSubscription.unsubscribe();
    }
    if(this.shopSubscription) {
      this.shopSubscription.unsubscribe();
    }
  }

}
